import type { ExcludesFalseyTypeGuard } from '../../../utils';
import type { DispatchFocusFn } from './focusElement';
import type { StepQuirks } from 'clickim/src/common/models/step';
import type { IMouseEvent } from 'clickim/src/common/models/mouseStep';
import type { ILocatedElement } from 'clickim/src/background/codeSnippets/getLocatedElement';

declare function getLocatedElement(locatedElement: ILocatedElement | string): Element | null;
declare const dispatchFocus: DispatchFocusFn;

export interface Modifiers { ctrl: boolean; alt: boolean; shift: boolean; meta: boolean }
interface DoClickEventData {
    isRoot?: boolean;
    locatedElement: ILocatedElement;
    events: IMouseEvent[];
    quirks?: StepQuirks;
    modifiers?: Partial<Modifiers>;
    button?: number;
}
interface Context extends Pick<DoClickEventData, 'events' | 'quirks' | 'modifiers' | 'button'> {
    element: Element;
}

export const doClick = (eventData: DoClickEventData, done: (data: { status: 'done' | 'failed'; result?: string; success: boolean }) => void) => {
    const eventConstructorSupported = typeof Event === 'function';

    window.__unloadNavigator = resolve;

    window.addEventListener('unload', window.__unloadNavigator);

    function handleReactSelectFocusQuirk(context: Context, event: MouseEvent) {
        function focusReactSelect(element: Element) {
            function getReactSelectRoot() {
                return getParents(element).find((el) => [...(el as Element).classList || []].includes('Select-control'));
            }

            function getReactSelectInput() {
                const root = getReactSelectRoot();
                return root ? root.querySelector('input') : null;
            }

            const reactSelectInput = getReactSelectInput();
            reactSelectInput?.focus();
        }

        const isReactSelectElement = context.quirks?.isReactSelect;
        if (event.type === 'mousedown' && isReactSelectElement) {
            focusReactSelect(context.element);
        }
    }

    function handleCKEditorQuirk(context: Context, event: MouseEvent) {
        const isCKEditorFrame = context.quirks?.isCKEditorFrame;
        if (event.type === 'click' && isCKEditorFrame) {
            document.body.focus();
        }
    }

    function getParents(el: ParentNode) {
        const parents: ParentNode[] = [];
        while (el?.parentNode) {
            parents.push(el);
            el = el.parentNode;
        }
        return parents;
    }

    function resolve(result?: string) {
        const status = {
            status: 'done',
            result,
            success: true,
            // @ts-expect-error isNonTextableElemnet doesn't exist here - looks like copy-paste that should be removed
            ...(context.isNonTextableElemnet && { reason: 'Set text on non input element' }), // eslint-disable-line @typescript-eslint/no-use-before-define
        };

        done(status);
    }

    function reject(result: string) {
        result ||= '';
        const status = {
            status: 'failed' as const,
            result,
            success: false,
        };

        done(status);
    }

    function dispatchMouseOver(element: Element) {
        const event = eventConstructorSupported ? new Event('mouseover', { composed: true }) : document.createEvent('Events');
        event.initEvent('mouseover', true, true);
        element.dispatchEvent(event);
    }

    function dispatchMouseMove(element: Element) {
        const modifiers = {};
        const rect = element.getBoundingClientRect();
        const clientX = rect.left + (rect.width / 2);
        const clientY = rect.top + (rect.height / 2);
        const button = 0;
        const eventType = 'mousemove';
        const event = createMouseEvent(eventType, modifiers, clientX, clientY, button);
        element.dispatchEvent(event);
    }

    function getEventPosition(userEvent: IMouseEvent, element: Element) {
        function isWithinBounds(start: number, end: number, point: number) {
            return (point > start) && (point < end);
        }

        const pointerPosition = userEvent.pointerPosition || {} as Partial<NonNullable<IMouseEvent['pointerPosition']>>;
        const rect = element.getBoundingClientRect();
        const clientX = pointerPosition.originX && isWithinBounds(rect.left, rect.left + rect.width, pointerPosition.originX) ? pointerPosition.originX : rect.left + (rect.width / 2);
        const clientY = pointerPosition.originY && isWithinBounds(rect.top, rect.top + rect.height, pointerPosition.originY) ? pointerPosition.originY : rect.top + (rect.height / 2);
        return { x: clientX, y: clientY };
    }

    function getEventDictionary(modifiers: Partial<Modifiers>, clientX: number, clientY: number): PointerEventInit {
        return {
            screenX: 0,
            screenY: 0,
            clientX,
            clientY,
            ctrlKey: Boolean(modifiers.ctrl),
            altKey: Boolean(modifiers.alt),
            shiftKey: Boolean(modifiers.shift),
            metaKey: Boolean(modifiers.meta),
            bubbles: true,
            cancelable: true,
            composed: true,
        };
    }

    function createPointerEvent(eventType: string, modifiers: Partial<Modifiers>, clientX: number, clientY: number) {
        if (!window.PointerEvent) {
            return undefined;
        }
        const eventProperties = getEventDictionary(modifiers, clientX, clientY);
        eventProperties.pointerType = 'mouse';
        eventProperties.isPrimary = true;
        return new window.PointerEvent(eventType, eventProperties);
    }

    function createMouseEvent(eventType: string, modifiers: Partial<Modifiers>, clientX: number, clientY: number, button: number) {
        const event = eventConstructorSupported ? new MouseEvent('click', { composed: true }) : document.createEvent('MouseEvents');
        event.initMouseEvent(
            eventType,
            true,
            true,
            document.defaultView!,
            1,
            0,
            0,
            clientX,
            clientY,
            Boolean(modifiers.ctrl),
            Boolean(modifiers.alt),
            Boolean(modifiers.shift),
            Boolean(modifiers.meta),
            button,
            document.body ? document.body.parentNode : document.documentElement);
        return event;
    }

    function getMouseEvent(userEvent: IMouseEvent, context: Context) {
        const pointerEvents = ['pointerup', 'pointerdown', 'pointermove'];
        const modifiers = context.modifiers || {};
        const pos = getEventPosition(userEvent, context.element);
        const button = context.button || 0;
        const eventType = userEvent.event;
        if (pointerEvents.includes(eventType)) {
            return createPointerEvent(eventType, modifiers, pos.x, pos.y);
        }
        return createMouseEvent(eventType, modifiers, pos.x, pos.y, button);
    }

    function findEffectiveActiveElement() {
        let activeElement = document.activeElement!;
        while (activeElement.shadowRoot?.activeElement) {
            activeElement = activeElement.shadowRoot.activeElement;
        }
        return activeElement;
    }

    function executeSynchronousEventSequence(context: Context) {
        context.events
            .map((userEvent) => {
                try {
                    return getMouseEvent(userEvent, context);
                } catch (e) {
                    // pointer events not supported :shrug:
                    return undefined;
                }
            })
            .filter(Boolean as any as ExcludesFalseyTypeGuard)
            .forEach((event) => {
                context.element.dispatchEvent(event);
                handleReactSelectFocusQuirk(context, event);
                handleCKEditorQuirk(context, event);
            });

        if (window.__unloadNavigator) {
            window.removeEventListener('unload', window.__unloadNavigator);
        }
    }

    const element = eventData.isRoot ? document.documentElement : getLocatedElement(eventData.locatedElement)!;
    const context = {
        element,
        events: eventData.events,
        quirks: eventData.quirks,
        modifiers: eventData.modifiers,
        button: eventData.button,
    };

    if (!context.element) {
        reject('element not found');
        return;
    }

    dispatchMouseOver(context.element);
    dispatchMouseMove(context.element);
    try {
        executeSynchronousEventSequence(context);
        const oldActiveElement = findEffectiveActiveElement();
        const quirks = context.quirks;
        const isReactSelectElement = quirks?.isReactSelect;
        const isCKEditorFrame = quirks?.isCKEditorFrame;
        if (!isReactSelectElement && !isCKEditorFrame) {
            // @ts-expect-error we never call doClick with such a property, likely a bad copy-paste
            dispatchFocus(eventData.elementToFocusLocatedElement, oldActiveElement);
        }
        resolve();
    } catch (e) {
        reject(e.toString());
    }
};
